import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import api from '../config/axios';

export interface NotificationData {
  _id?: string;
  type: 'new_order' | 'order_updated' | 'order_cancelled' | 'payment_success' | 'table_reservation' | 'guest_order' | 'merged_payment_success';
  orderId?: string;
  reservationId?: string;
  customerName?: string;
  tableName?: string;
  items?: Array<{
    name: string;
    quantity: number;
  }>;
  total?: number;
  message: string;
  timestamp: Date | string;
  phoneNumber?: string;
  reservationTime?: string;
  numberOfGuests?: number;
  mergedOrders?: string[];
  orderCount?: number;
  isRead?: boolean;
  recipientRole?: string;
  recipientId?: string;
  createdAt?: Date | string;
}

interface NotificationContextType {
  notifications: NotificationData[];
  unreadCount: number;
  addNotification: (notification: NotificationData) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  markAllAsRead: () => void;
  markAsRead: (id: string) => void;
  socket: Socket | null;
  connectSocket: (staffId?: string, isAdmin?: boolean) => void;
  disconnectSocket: () => void;
  isConnected: boolean;
  loadNotifications: () => Promise<void>;
  loading: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/notifications?limit=50');
      if (response.data.success) {
        setNotifications(response.data.data.notifications);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const addNotification = (notification: NotificationData) => {
    const newNotification = { ...notification, isRead: false };
    setNotifications(prev => {
      const exists = prev.some(n => {
        if (notification.orderId) {
          return n.orderId === notification.orderId && n.type === notification.type;
        } else if (notification.reservationId) {
          return n.reservationId === notification.reservationId && n.type === notification.type;
        }
        return false;
      });
      if (exists) {
        console.log('Notification already exists, skipping:', notification.orderId || notification.reservationId);
        return prev;
      }
      return [newNotification, ...prev];
    });
  };

  const removeNotification = async (id: string) => {
    try {
      if (!id) {
        console.error('Notification ID is undefined');
        return;
      }
      await api.delete(`/api/notifications/${id}`);
      setNotifications(prev => prev.filter(notif => notif._id !== id));
    } catch (error) {
      console.error('Error removing notification:', error);
    }
  };

  const clearNotifications = async () => {
    try {
      await api.delete('/api/notifications/delete-read');
      setNotifications(prev => prev.filter(notif => !notif.isRead));
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.patch('/api/notifications/mark-all-read');
      setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await api.patch(`/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(notif => 
        notif._id === id ? { ...notif, isRead: true } : notif
      ));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const unreadCount = notifications.filter(notif => !notif.isRead).length;

  const connectSocket = (staffId?: string, isAdmin?: boolean) => {
    if (isConnecting) {
      console.log('Already connecting, skipping...');
      return;
    }

    if (socket && socket.connected) {
      console.log('Socket already connected, skipping...');
      return;
    }

    if (isConnected) {
      console.log('Already connected, skipping...');
      return;
    }

    setIsConnecting(true);

    if (socket) {
      socket.disconnect();
    }

    console.log('Connecting to notification server...');
    const newSocket = io(process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001', {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: false
    });
    
    newSocket.on('connect', () => {
      console.log('Connected to notification server, socket ID:', newSocket.id);
      setIsConnected(true);
      setIsConnecting(false);
      
      if (isAdmin) {
        newSocket.emit('join-admin');
        console.log('Joined admin room');
      } else if (staffId) {
        newSocket.emit('join-staff', staffId);
        console.log('Joined staff room for:', staffId);
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setIsConnecting(false);
    });

    newSocket.on('new-order-notification', (notification: NotificationData) => {
      console.log('New order notification received:', notification);
      addNotification(notification);
      playNotificationSound();
    });

    newSocket.on('order-update-notification', (notification: NotificationData) => {
      console.log('Order update notification received:', notification);
      addNotification(notification);
    });

    newSocket.on('order-cancelled-notification', (notification: NotificationData) => {
      console.log('Order cancelled notification received:', notification);
      addNotification(notification);
    });

    newSocket.on('payment-success-notification', (notification: NotificationData) => {
      console.log('Payment success notification received:', notification);
      addNotification(notification);
      playPaymentSuccessSound();
    });

    newSocket.on('table-reservation-notification', (notification: NotificationData) => {
      console.log('Table reservation notification received:', notification);
      addNotification(notification);
    });

    newSocket.on('guest-order-notification', (notification: NotificationData) => {
      console.log('Guest order notification received:', notification);
      addNotification(notification);
    });

    newSocket.on('merged-payment-success-notification', (notification: NotificationData) => {
      console.log('Merged payment success notification received:', notification);
      addNotification(notification);
      playMergedPaymentSuccessSound();
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Disconnected from notification server:', reason);
      setIsConnected(false);
    });

    setSocket(newSocket);
  };

  const disconnectSocket = () => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
  };

  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Could not play notification sound:', error);
    }
  };

  const playPaymentSuccessSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(523, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.2);
      oscillator.frequency.setValueAtTime(1047, audioContext.currentTime + 0.3);
      
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.4);
    } catch (error) {
      console.log('Could not play payment success sound:', error);
    }
  };

  const playMergedPaymentSuccessSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(523, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.2);
      oscillator.frequency.setValueAtTime(1047, audioContext.currentTime + 0.3);
      oscillator.frequency.setValueAtTime(1319, audioContext.currentTime + 0.4);
      oscillator.frequency.setValueAtTime(1568, audioContext.currentTime + 0.5);
      
      gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.6);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.6);
    } catch (error) {
      console.log('Could not play merged payment success sound:', error);
    }
  };

  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [socket]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    addNotification,
    removeNotification,
    clearNotifications,
    markAllAsRead,
    markAsRead,
    socket,
    connectSocket,
    disconnectSocket,
    isConnected,
    loadNotifications,
    loading,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}; 